
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Timer, Coins, Users, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useLaunchPool } from "./LaunchPanelContract";

interface LaunchPanelProps {
  startPrice?: number;
  endPrice?: number;
  duration?: number;
  tokenAddress?: string;
}

const LaunchPanel = ({
  startPrice = 0.01,
  endPrice = 0.001,
  duration = 3600, // 1 hour in seconds
  tokenAddress = "0x0000000000000000000000000000000000000000", // Default to zero address
}: LaunchPanelProps) => {
  const { toast } = useToast();
  
  // Use the custom hook to interact with the blockchain
  const {
    launchPoolData,
    commitment,
    setCommitment,
    handleCommit,
    isCommitting,
    isError,
    isFinalized,
    handleFinalize,
    handleClaim,
    isFinalizing,
    isClaiming,
  } = useLaunchPool(tokenAddress);

  // Format time remaining to hours and minutes
  const formatTimeRemaining = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m remaining`;
  };

  // Calculate progress percentage
  const progressPercentage = (parseFloat(launchPoolData.totalCommitment) / parseFloat(launchPoolData.targetCommitment)) * 100;
  
  // Show fallback UI if there's an error
  if (isError) {
    return (
      <Card className="glass-card p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium">dutch auction & pre-commitment</h3>
        </div>
        <div className="p-8 text-center text-muted-foreground">
          <p>Unable to load launch pool data. Please try again later.</p>
          <Button 
            onClick={() => window.location.reload()} 
            variant="outline" 
            className="mt-4"
          >
            Refresh
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="glass-card p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">dutch auction & pre-commitment</h3>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Timer className="h-4 w-4" />
          <span>{formatTimeRemaining(launchPoolData.timeRemaining)}</span>
        </div>
      </div>

      <div className="space-y-4">
        <div className="bg-muted/50 rounded-lg p-3">
          <div className="flex justify-between text-sm mb-2">
            <span>Current Price</span>
            <span className="font-medium">{parseFloat(launchPoolData.currentPrice).toFixed(6)} MATIC</span>
          </div>
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Starting: {launchPoolData.startPrice} MATIC</span>
            <span>Floor: {launchPoolData.endPrice} MATIC</span>
          </div>
        </div>

        <div>
          <label className="text-sm text-muted-foreground">Commit Amount (MATIC)</label>
          <Input
            type="number"
            placeholder="Enter amount to commit"
            value={commitment}
            onChange={(e) => setCommitment(e.target.value)}
            className="mt-1"
            disabled={isCommitting}
          />
        </div>

        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Total Committed</span>
            <span>
              {parseFloat(launchPoolData.totalCommitment).toLocaleString()} / {parseFloat(launchPoolData.targetCommitment).toLocaleString()} MATIC
            </span>
          </div>
          <Progress value={progressPercentage} className="h-1" />
        </div>

        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-1">
            <Users className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Participants:</span>
          </div>
          <span>{launchPoolData.participantCount}</span>
        </div>

        {!isFinalized && (launchPoolData.timeRemaining <= 0 || parseFloat(launchPoolData.totalCommitment) >= parseFloat(launchPoolData.targetCommitment)) && <Button onClick={handleFinalize} className="w-full" disabled={isFinalizing}>{isFinalizing ? "Finalizing..." : "Finalize Launch"}</Button>}
        {isFinalized && <Button onClick={handleClaim} className="w-full" disabled={isClaiming}>{isClaiming ? "Claiming..." : "Claim Fair-Launch Tokens"}</Button>}
        <Button 
          onClick={handleCommit} 
          className="w-full"
          variant="default"
          disabled={isCommitting || isFinalized || launchPoolData.timeRemaining <= 0}
        >
          {isCommitting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <Coins className="h-4 w-4 mr-2" />
              Commit to Launch Pool
            </>
          )}
        </Button>
      </div>
    </Card>
  );
};

export default LaunchPanel;
